<script>
import { required } from '@vuelidate/validators';
import { useVuelidate } from '@vuelidate/core';
import router from '../../../../index';
import { useAlert } from 'dashboard/composables';
import LoadingState from 'dashboard/components/widgets/LoadingState.vue';
import PageHeader from '../../SettingsSubPageHeader.vue';
import axios from 'axios';
import { mapGetters } from 'vuex';

export default {
  components: {
    LoadingState,
    PageHeader,
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      inbox_name: '',
      isCreating: false,
      showQRCode: false,
      qrCodeData: '',
      integrationId: '',
      accessToken: '',
      isLoading: false,
      errorMessage: '',
      qrStatus: 'waiting_qr_scan',
      statusMessage: '',
      statusCheckInterval: null,
      qrStatusInterval: null,
    };
  },
  validations: {
    inbox_name: { required },
  },
  computed: {
    ...mapGetters({
      currentUser: 'getCurrentUser',
      currentAccountId: 'getCurrentAccountId',
    }),
    microserviceUrl() {
      return (
        window.hichatConfig?.zaloPersonalMicroserviceUrl ||
        'http://localhost:8000'
      );
    },
  },
  mounted() {
    // Tự động bắt đầu quá trình đăng ký khi component load
    this.startRegistration();
  },
  beforeUnmount() {
    // Clear intervals khi component bị destroy
    if (this.statusCheckInterval) {
      clearInterval(this.statusCheckInterval);
    }
    if (this.qrStatusInterval) {
      clearInterval(this.qrStatusInterval);
    }
  },
  methods: {
    showAlert(message) {
      useAlert(message);
    },
    
    async startRegistration() {
      try {
        this.isLoading = true;
        this.errorMessage = '';
        this.statusMessage = 'Đang khởi tạo kết nối...';
        
        // Gọi API register
        const registerResponse = await axios.post(
          `${this.microserviceUrl}/api/register`,
          {
            hichat_account_id: this.currentAccountId.toString(),
            hichat_inbox_id: '', // Sẽ cập nhật sau
            hichat_api_key: this.currentUser.access_token,
            hichat_base_url: window.location.origin,
            agent_id: this.currentUser.id.toString(),
          }
        );

        if (registerResponse.data.success) {
          this.qrCodeData = registerResponse.data.qr_code;
          this.integrationId = registerResponse.data.integration_id;
          this.accessToken = registerResponse.data.access_token;
          this.statusMessage = registerResponse.data.message || 'Vui lòng quét mã QR';
          
          this.showQRCode = true;
          
          // Bắt đầu kiểm tra QR status
          this.startQRStatusCheck();
        } else {
          throw new Error(registerResponse.data.message || 'Đăng ký thất bại');
        }
      } catch (error) {
        this.errorMessage = error.message || 'Có lỗi xảy ra khi khởi tạo';
        this.showAlert(this.errorMessage);
      } finally {
        this.isLoading = false;
      }
    },

    startQRStatusCheck() {
      // Kiểm tra QR status mỗi 2 giây
      this.qrStatusInterval = setInterval(async () => {
        try {
          const response = await axios.get(
            `${this.microserviceUrl}/api/qr-status/${this.integrationId}`
          );
          
          if (response.data.success) {
            this.qrStatus = response.data.status;
            this.statusMessage = response.data.message;
            
            if (
              response.data.status === 'logged_in' ||
              response.data.is_connected
            ) {
              // QR đã được scan và đăng nhập thành công
              clearInterval(this.qrStatusInterval);
              
              // Lấy access_token từ response nếu có
              if (response.data.access_token) {
                this.accessToken = response.data.access_token;
              }
              
              // Lấy thông tin chi tiết từ microservice
              await this.fetchUserInfo();
            } else if (response.data.status === 'new_qr') {
              // Có QR mới, cập nhật QR code
              if (response.data.qr_code) {
                this.qrCodeData = response.data.qr_code;
                this.statusMessage =
                  response.data.message || 'Có QR mới, vui lòng quét mã QR mới';
              }
            } else if (response.data.status === 'login_failed') {
              clearInterval(this.qrStatusInterval);
              this.errorMessage = 'Đăng nhập thất bại. Vui lòng thử lại.';
            }
          }
        } catch (error) {
          // Tiếp tục check, không dừng vì lỗi tạm thời
        }
      }, 2000);
    },

    async fetchUserInfo() {
      try {
        this.statusMessage = 'Đang lấy thông tin người dùng...';
        
        const response = await axios.get(
          `${this.microserviceUrl}/api/status/${this.integrationId}`,
          {
            headers: {
              'X-Access-Token': this.accessToken,
            },
          }
        );
        
        if (response.data) {
          // Tạo inbox với thông tin từ microservice
          await this.createInbox(response.data);
        } else {
          throw new Error('Không thể lấy thông tin người dùng');
        }
      } catch (error) {
        this.errorMessage = error.message || 'Lỗi khi lấy thông tin người dùng';
        this.showAlert(this.errorMessage);
      }
    },

    async createInbox(userData) {
      try {
        this.isCreating = true;
        this.statusMessage = 'Đang tạo hộp thư...';
        
        const response = await this.$store.dispatch(
          'inboxes/createZaloPersonalChannel',
          {
            user_id: userData.id || '',
            display_name: userData.display_name || 'Zalo Personal',
            imei: userData.imei || '',
            inbox_name: 
              this.inbox_name ||
              `Zalo Personal - ${userData.display_name || ''}`,
            session_data: JSON.stringify({
              integration_id: this.integrationId,
              access_token: this.accessToken,
              user_info: {
                id: userData.id,
                name: userData.display_name,
                phone: userData.phone || '',
              },
              created_at: new Date().toISOString(),
            }),
          }
        );

        // Cập nhật inbox_id cho microservice
        await axios.post(
          `${this.microserviceUrl}/api/integration/${this.integrationId}/update`,
          {
            hichat_inbox_id: response.id.toString(),
          },
          {
            headers: {
              'X-Access-Token': this.accessToken,
            },
          }
        );

        this.showAlert('Tạo hộp thư Zalo Personal thành công!');
        router.replace({
          name: 'settings_inboxes_add_agents',
          params: { page: 'new', inbox_id: response.id },
        });
      } catch (error) {
        let errorMessage = 'Có lỗi xảy ra khi tạo hộp thư';
        if (error?.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error.message) {
          errorMessage = error.message;
        }
        this.errorMessage = errorMessage;
        this.showAlert(errorMessage);
      } finally {
        this.isCreating = false;
      }
    },

    retryRegistration() {
      // Reset state và thử lại
      this.showQRCode = false;
      this.errorMessage = '';
      this.qrCodeData = '';
      this.integrationId = '';
      this.accessToken = '';
      
      if (this.statusCheckInterval) {
        clearInterval(this.statusCheckInterval);
      }
      if (this.qrStatusInterval) {
        clearInterval(this.qrStatusInterval);
      }
      
      this.startRegistration();
    },
  },
};
</script>

<template>
  <div class="wizard-body w-[75%] flex-shrink-0 flex-grow-0 max-w-[75%]">
    <PageHeader
      :header-title="$t('INBOX_MGMT.ADD.ZALO_PERSONAL.TITLE')"
      :header-content="$t('INBOX_MGMT.ADD.ZALO_PERSONAL.DESC')"
    />

    <!-- Cảnh báo quan trọng -->
    <div class="warning-banner">
      <div class="warning-content">
        <i class="fluent-icon fluent-icon-warning"></i>
        <p>{{ $t('INBOX_MGMT.ADD.ZALO_PERSONAL.WARNING_MESSAGE') }}</p>
      </div>
    </div>

    <!-- Loading state khi đang khởi tạo -->
    <div v-if="isLoading && !showQRCode" class="text-center p-8">
      <LoadingState :message="statusMessage" />
    </div>

    <!-- QR Code state -->
    <div v-else-if="showQRCode" class="qr-code-container text-center">
      <h3>{{ $t('INBOX_MGMT.ADD.ZALO_PERSONAL.SCAN_QR') }}</h3>

      <div class="qr-code-wrapper">
        <img :src="qrCodeData" alt="QR Code" class="qr-code" />

        <!-- Retry button overlay khi có lỗi -->
        <div
          v-if="errorMessage"
          class="qr-retry-overlay"
          @click="retryRegistration"
        >
          <div class="retry-button">
            <i class="fluent-icon fluent-icon-arrow-clockwise"></i>
            <span>{{ $t('INBOX_MGMT.ADD.ZALO_PERSONAL.RETRY') }}</span>
          </div>
        </div>
      </div>

      <!-- Status message -->
      <div class="status-info">
        <!-- Status indicator -->
        <div class="status-indicator">
          <span v-if="qrStatus === 'waiting_qr_scan'" class="status-waiting">
            {{ $t('INBOX_MGMT.ADD.ZALO_PERSONAL.STATUS.WAITING_QR_SCAN') }}
          </span>
          <span v-else-if="qrStatus === 'qr_scanned'" class="status-scanned">
            {{ $t('INBOX_MGMT.ADD.ZALO_PERSONAL.STATUS.QR_SCANNED') }}
          </span>
          <span v-else-if="qrStatus === 'logged_in'" class="status-success">
            {{ $t('INBOX_MGMT.ADD.ZALO_PERSONAL.STATUS.LOGGED_IN') }}
          </span>
          <span v-else-if="qrStatus === 'login_failed'" class="status-failed">
            {{ $t('INBOX_MGMT.ADD.ZALO_PERSONAL.STATUS.LOGIN_FAILED') }}
          </span>
          <span v-else-if="qrStatus === 'NEW_QR'" class="status-waiting">
            {{ $t('INBOX_MGMT.ADD.ZALO_PERSONAL.STATUS.NEW_QR') }}
          </span>
        </div>
      </div>

      <!-- Loading khi đang tạo inbox -->
      <LoadingState v-if="isCreating" message="Đang tạo hộp thư..." />
    </div>

    <!-- Form nhập tên inbox (optional) -->
    <form
      v-else
      class="mx-0 flex flex-wrap"
      @submit.prevent="startRegistration"
    >
      <div class="w-[60%]">
        <div class="w-full">
          <label>
            {{ $t('INBOX_MGMT.ADD.ZALO_PERSONAL.INBOX_NAME.LABEL') }}
            <input
              v-model.trim="inbox_name"
              type="text"
              :placeholder="
                $t('INBOX_MGMT.ADD.ZALO_PERSONAL.INBOX_NAME.PLACEHOLDER')
              "
            />
            <span class="help-text">
              {{ $t('INBOX_MGMT.ADD.ZALO_PERSONAL.INBOX_NAME.HELP') }}
            </span>
          </label>
        </div>

        <div class="w-full text-right">
          <input
            type="submit"
            :value="$t('INBOX_MGMT.ADD.ZALO_PERSONAL.START_CONNECTION')"
            class="button"
            :disabled="isLoading"
          />
        </div>
      </div>
    </form>
  </div>
</template>

<style scoped>
.warning-banner {
  background: linear-gradient(135deg, #fff3cd 0%, #ffeaa7 100%);
  border: 2px solid #f39c12;
  border-radius: 12px;
  padding: 1.5rem;
  margin: 2rem 0;
  box-shadow: 0 4px 12px rgba(243, 156, 18, 0.2);
}

.warning-content {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
}

.warning-content i {
  color: #f39c12;
  font-size: 24px;
  margin-top: 2px;
  flex-shrink: 0;
}

.warning-content p {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #8b5a00;
  line-height: 1.5;
  text-align: left;
}

.qr-code-container {
  padding: 2rem;
  max-width: 500px;
  margin: 0 auto;
}

.qr-code-wrapper {
  background: white;
  padding: 1rem;
  border-radius: 8px;
  display: inline-block;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  margin: 1rem 0;
  position: relative;
}

.qr-code {
  max-width: 300px;
  height: auto;
  border-radius: 4px;
}

.qr-retry-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  cursor: pointer;
}

.retry-button {
  background: white;
  color: #333;
  padding: 0.75rem 1.5rem;
  border-radius: 6px;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 500;
  font-size: 14px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: 1px solid #ddd;
}

.retry-button:hover {
  background: #f5f5f5;
}

.retry-button i {
  font-size: 16px;
}

.status-info {
  margin: 1.5rem 0;
}

.status-message {
  font-size: 16px;
  margin-bottom: 1rem;
  color: var(--color-body);
}

.status-indicator {
  margin: 1rem 0;
}

.status-indicator span {
  display: inline-block;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-weight: 500;
  font-size: 14px;
}

.status-waiting {
  background-color: #e3f2fd;
  color: #1976d2;
}

.status-scanned {
  background-color: #fff3e0;
  color: #f57c00;
}

.status-success {
  background-color: #e8f5e8;
  color: #388e3c;
}

.status-failed {
  background-color: #ffebee;
  color: #d32f2f;
}

.help-text {
  font-size: 12px;
  color: var(--s-600);
  margin-top: 0.25rem;
  display: block;
}

.button.secondary {
  background: var(--s-200);
  color: var(--s-800);
  border: 1px solid var(--s-300);
}

.button.secondary:hover {
  background: var(--s-300);
}
</style> 